import { useState } from 'react'
import { useCreateWpToken } from '@/api/hooks/use-wordpress'
import { CopyButton } from '@/components/ui/copy-button'
import { Modal } from '@/components/ui/modal'

type Ttl = '1h' | '24h' | '7d' | '30d' | 'never'

const TTL_OPTIONS: Array<{ value: Ttl; label: string }> = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'never', label: 'Never' },
]

interface Props {
  onClose: () => void
}

export function CreateWpTokenModal({ onClose }: Props): React.JSX.Element {
  const createToken = useCreateWpToken()
  const [name, setName] = useState('')
  const [ttl, setTtl] = useState<Ttl>('24h')
  const [createdToken, setCreatedToken] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    createToken.mutate({ name, ttl }, { onSuccess: (res) => setCreatedToken(res.token) })
  }

  if (createdToken) {
    return (
      <Modal open onClose={onClose} title="Connection Token Created">
        <div className="space-y-4">
          <div className="rounded-lg border border-warning/30 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-800">
              Copy this token now and paste it in your WordPress plugin settings. It will not be
              shown again.
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-surface-1 p-3">
            <code className="flex-1 break-all font-mono text-sm select-all">{createdToken}</code>
            <CopyButton value={createdToken} />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} title="Connect WordPress Site">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="wp-name" className="block text-sm font-medium text-text-primary">
            Connection name
          </label>
          <input
            id="wp-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Company Blog, News Site"
            className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-text-primary">Token expires in</span>
          <div className="mt-1 grid grid-cols-5 gap-2">
            {TTL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTtl(opt.value)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                  ttl === opt.value
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-surface-3 text-text-secondary hover:bg-surface-1'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-text-tertiary">
            The token is single-use. Once your WordPress site connects, it cannot be reused.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || createToken.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {createToken.isPending ? 'Creating...' : 'Generate Token'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
